// Navigation sidebar — workspace selector, page links, user menu, and sign-out.
// Renders collapsed on mobile and expanded on desktop via useMediaQuery.

import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { SETTINGS_NAV_ITEM, getActiveNavView, getVisibleNavItems, getViewFromPathname, getCampaignsNavEntry, PATH_FOR_VIEW } from './navigationConfig';
import { useAuth } from '../../contexts/AuthContext';
import WorkspaceSelector from './WorkspaceSelector';
import BuildVersionLink from './BuildVersionLink';
import buildInfo from '../../buildInfo.json';
import Badge from './primitives/Badge';
import Button from './primitives/Button';
import { resolveViewerRole } from '../features/automation/automationGates';
import { useCampaignsAccess } from '../../hooks/useCampaignsAccess';
import { useDueTasksSummary as useDueTasksSummaryDefault } from '../../store/hooks/useDueTasksSummary';
import { useOverlaidUnreadMap } from '../../store/unreadOverlay';
import { getTodayIso } from '../../utils/formatting';
import { prefetchRoute, prefetchAutomationsPageV2, prefetchSettingsPage, prefetchKanbanView, prefetchTemplatesPage } from '../../utils/prefetchRoutes';
import { requestNavigate } from '../../navigation/leaveGuardRegistry';

const BUILD_VERSION = buildInfo?.version || 'n/a';

const SIDEBAR_NO_RETRY = Object.freeze([]);

// Review ui IN-02: the locked sheet (and the PlanLimitModal it wraps) loads on
// first open, so none of it ships in the boot chunk while Campaigns is hidden.
const LockedFeatureSheet = React.lazy(() => import('../features/automation/LockedFeatureSheet'));

// A failed chunk load leaves the sheet closed instead of taking the Sidebar
// down with it; the next full load fetches the chunk again.
class LockedSheetBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { failed: false };
    }

    static getDerivedStateFromError() {
        return { failed: true };
    }

    componentDidCatch(err) {
        console.error('Sidebar: the locked sheet failed to load', err);
        if (typeof this.props.onError === 'function') this.props.onError();
    }

    render() {
        return this.state.failed ? null : this.props.children;
    }
}

// D-05: hover/focus-prefetch lookup for the three routes not covered by
// App.js's idle top-3 (automations, settings) plus kanban, which has no
// standalone sidebar nav item of its own — it's a sub-view reached from
// 'transactions', so hovering that nav item warms both TransactionListV2
// (already idle-prefetched) and the KanbanView chunk.
const HOVER_PREFETCH_BY_VIEW = {
    automations: prefetchAutomationsPageV2,
    settings: prefetchSettingsPage,
    transactions: prefetchKanbanView,
    // Templates and its default Email tab are both lazy chunks (2026-10-04).
    templates: prefetchTemplatesPage,
};

const TASKS_BADGE_CAP = 99;

// Count text for the Tasks nav item: hidden at zero, capped at 99+ (A-12).
const formatDueCount = (due) => (due > TASKS_BADGE_CAP ? `${TASKS_BADGE_CAP}+` : String(due));

// Clients nav count (Phase 65 D-29): the sum of the workspace's per-contact
// unread-reply counts. A legacy non-number value (Timestamp) counts as 1.
const sumUnreadReplies = (map) => {
    if (!map || typeof map !== 'object') return 0;
    return Object.values(map).reduce((sum, v) => {
        if (typeof v === 'number') return sum + (Number.isFinite(v) && v > 0 ? v : 0);
        return v == null ? sum : sum + 1;
    }, 0);
};
const formatReplyCount = (n) => (n > 9 ? '9+' : String(n));
// The server's cap on unreadContactReplies; past it the total is named "500+".
const REPLY_OVERFLOW_AT = 500;

function Sidebar({
    isCollapsed,
    setIsCollapsed,
    isMobile = false,
    isOpen = false,
    onClose = () => {},
    canAccessManageWorkspace = false,
    isViewingChildAsParent = false,
    parentWorkspaceName: twName = '',
    childWorkspaceName: tawName = '',
    onBackToParent = null,
    workspaces = [],
    selectedWorkspaceId = '',
    onSelectWorkspace = () => {},
    onOpenWhatsNew = null,
    deps = {},
}) {
    const { isTcModeEnabled = false, user } = useAuth();
    const location = useLocation();
    const rrNavigate = useNavigate();
    const visibleNavItems = React.useMemo(
        () => getVisibleNavItems(isTcModeEnabled, canAccessManageWorkspace),
        [isTcModeEnabled, canAccessManageWorkspace]
    );
    const mobileNavItems = React.useMemo(() => [...visibleNavItems, SETTINGS_NAV_ITEM], [visibleNavItems]);
    // A-12: one-shot count of overdue plus due-today open tasks (no listener;
    // refetches on TASKS_CHANGED_EVENT). The hook no-ops without a workspace.
    const useDueTasksSummary = deps.useDueTasksSummary || useDueTasksSummaryDefault;
    const dueSummary = useDueTasksSummary(selectedWorkspaceId || '', { todayIso: getTodayIso(), countsOnly: true });
    const dueTasks = selectedWorkspaceId && !dueSummary?.error
        ? (dueSummary?.overdueCount || 0) + (dueSummary?.todayCount || 0)
        : 0;
    // Accessible name and tooltip for a nav item; only Tasks carries a count.
    const dueLabel = (item) => (item.view === 'tasks' && dueTasks > 0 ? `Tasks, ${dueTasks} due.` : null);
    // Phase 65 D-26, D-29: unread contact replies, summed across contacts. Read
    // from the selected workspace doc already loaded app-wide (a server-maintained
    // map, contactId -> count); no listener and no contacts load.
    const selectedWorkspace = workspaces.find((w) => w?.id === selectedWorkspaceId) || null;
    // When the server caps the map it sets unreadContactRepliesOverflow (strictly true);
    // the badge keeps the 9+ cap and names the total "500+". An absent flag is off.
    const replyOverflow = selectedWorkspace?.unreadContactRepliesOverflow === true;
    // D-37: pending optimistic read changes show through before the server's map lands.
    const replyMap = useOverlaidUnreadMap(selectedWorkspace?.unreadContactReplies, selectedWorkspaceId);
    const unreadReplyCount = replyOverflow
        ? Math.max(REPLY_OVERFLOW_AT, sumUnreadReplies(replyMap))
        : sumUnreadReplies(replyMap);
    // Phase 68 D-18: new leads fold into the same CRM badge. The workspace doc's
    // newLeadCount is server-owned; absent or malformed counts as 0.
    const rawLeadCount = selectedWorkspace?.newLeadCount;
    const newLeadCount = Number.isInteger(rawLeadCount) && rawLeadCount > 0 ? rawLeadCount : 0;
    const badgeTotal = unreadReplyCount + newLeadCount;
    const showsReplyDot = (item) => item.view === 'contacts' && badgeTotal > 0;
    // "{r} unread {reply|replies}, {l} new {lead|leads}"; a zero part is omitted.
    const replyLabelPart = replyOverflow
        ? `${REPLY_OVERFLOW_AT}+ unread replies`
        : `${unreadReplyCount} unread ${unreadReplyCount === 1 ? 'reply' : 'replies'}`;
    const leadLabelPart = `${newLeadCount} new ${newLeadCount === 1 ? 'lead' : 'leads'}`;
    const replyLabel = [unreadReplyCount > 0 ? replyLabelPart : null, newLeadCount > 0 ? leadLabelPart : null]
        .filter(Boolean)
        .join(', ');
    const renderReplyDot = (item, collapsed = false) => (showsReplyDot(item) ? (
        <span
            title={`CRM, ${replyLabel}`}
            data-testid="sidebar-reply-dot"
            className={`inline-flex shrink-0 items-center justify-center rounded-full font-semibold tabular-nums ${collapsed ? 'absolute right-1 top-1' : 'ml-auto'}`}
            style={{
                minWidth: '16px', height: '16px', padding: '0 4px', fontSize: '10px', lineHeight: 1,
                background: 'var(--color-accent)', color: 'var(--on-accent)',
            }}
        >
            <span aria-hidden="true">{formatReplyCount(badgeTotal)}</span>
            <span className="sr-only">{`CRM, ${replyLabel}`}</span>
        </span>
    ) : null);
    const renderDueBadge = (item) => (item.view === 'tasks' && dueTasks > 0 ? (
        <Badge size="sm" className="sidebar__due-badge ml-auto tabular-nums" aria-hidden="true">
            {formatDueCount(dueTasks)}
        </Badge>
    ) : null);
    // Phase 64 (D-03, D-10) and 66 D-22: the Campaigns entry follows the same
    // rule as the page (useCampaignsAccess). On or Paused shows it; Hidden
    // shows it only for a workspace on the rollout allowlist. A Hidden
    // workspace outside the rollout gets an empty usage id, so it pays no
    // usage call and nothing renders. The sidebar does not retry a failed
    // usage read; the page owns that.
    const campaignsAccess = useCampaignsAccess(selectedWorkspaceId || '', {
        useAutomationSwitches: deps.useAutomationSwitches,
        useWorkspaceUsage: deps.useWorkspaceUsage,
        useCampaignRollout: deps.useCampaignRollout,
        retryDelaysMs: SIDEBAR_NO_RETRY,
    });
    const usageEntry = campaignsAccess.usageEntry;
    const campaignsEntry = getCampaignsNavEntry({
        visible: campaignsAccess.loaded && campaignsAccess.visible,
        entitlement: campaignsAccess.entitlement,
    });
    const [lockedSheetOpen, setLockedSheetOpen] = React.useState(false);
    const lockedTriggerRef = React.useRef(null);
    const lockedViewerRole = resolveViewerRole({ workspace: selectedWorkspace, uid: user?.uid, usage: usageEntry?.payload });
    const lockedOwnerName = (() => {
        const ownerId = selectedWorkspace?.ownerId;
        const name = ownerId ? selectedWorkspace?.memberDirectory?.[ownerId]?.displayName : '';
        return typeof name === 'string' && name.trim() ? name.trim() : null;
    })();
    const closeLockedSheet = () => {
        setLockedSheetOpen(false);
        // Return focus to the entry that opened the sheet.
        const trigger = lockedTriggerRef.current;
        if (trigger && typeof trigger.focus === 'function') trigger.focus();
    };
    // Only one locked entry is mounted at a time (desktop or phone), so a
    // single ref serves both layouts. The sheet opens over the phone menu,
    // which stays open so focus can return to the entry.
    const renderCampaignsLocked = (phone) => {
        if (!campaignsEntry) return null;
        // Review ui WR-07: the collapsed desktop rail shows the icon only, with
        // the label as a tooltip, like the sibling items.
        const collapsedDesktop = !phone && isCollapsed;
        if (!campaignsEntry.locked) {
            // Entitled tier (D-28): a real nav item that routes to /campaigns.
            const active = activeNavView === campaignsEntry.view;
            return (
                <Button
                    key={campaignsEntry.key}
                    variant="link"
                    onClick={() => handleNavigate(campaignsEntry.view)}
                    className={`sidebar__item ${active ? 'sidebar__item--active' : ''} w-full no-underline hover:no-underline ${phone ? 'min-h-[44px]' : ''}`}
                    aria-current={active ? 'page' : undefined}
                    title={collapsedDesktop ? campaignsEntry.label : ''}
                    aria-label={collapsedDesktop ? campaignsEntry.label : undefined}
                >
                    {campaignsEntry.icon}
                    <span className={phone ? undefined : 'sidebar__item-label'}>{phone ? campaignsEntry.mobileLabel : campaignsEntry.label}</span>
                </Button>
            );
        }
        return (
            <Button
                key={campaignsEntry.key}
                ref={lockedTriggerRef}
                variant="link"
                onClick={() => setLockedSheetOpen(true)}
                className={`sidebar__item w-full no-underline hover:no-underline ${phone ? 'min-h-[44px]' : ''}`}
                aria-label={campaignsEntry.ariaLabel}
                aria-haspopup="dialog"
                title={collapsedDesktop ? campaignsEntry.label : ''}
            >
                {campaignsEntry.icon}
                <span className={phone ? undefined : 'sidebar__item-label'}>{phone ? campaignsEntry.mobileLabel : campaignsEntry.label}</span>
                {/* The collapsed rail keeps only the icon, like every other item. */}
                {!collapsedDesktop && (
                    <Lock size={16} aria-hidden="true" className="ml-auto" style={{ color: 'var(--sidebar-text-quiet)' }} />
                )}
            </Button>
        );
    };
    const lockedSheet = campaignsEntry && campaignsEntry.locked && lockedSheetOpen ? (
        <LockedSheetBoundary onError={() => setLockedSheetOpen(false)}>
            <React.Suspense fallback={null}>
                <LockedFeatureSheet
                    feature="campaigns"
                    state="upgrade"
                    viewerRole={lockedViewerRole}
                    ownerName={lockedOwnerName}
                    onClose={closeLockedSheet}
                />
            </React.Suspense>
        </LockedSheetBoundary>
    ) : null;
    // Position: right after the Automations item.
    const withCampaigns = (items, renderItem, phone) => items.flatMap((item) => (
        item.view === 'automations' ? [renderItem(item), renderCampaignsLocked(phone)] : [renderItem(item)]
    ));
    const currentView = getViewFromPathname(location.pathname);
    const activeNavView = getActiveNavView(currentView);
    // Every Sidebar navigation asks the app-wide leave guard first (owner
    // request, Phase 67 walkthrough): a page with unsaved work, such as the
    // campaign builder, shows its "Leave without saving?" confirm and the move
    // happens only on Leave. With nothing unsaved it navigates at once. The
    // phone menu closes first, so the confirm is not hidden behind it.
    const handleNavigate = (view) => {
        const path = PATH_FOR_VIEW[view] ?? '/';
        if (isMobile) {
            onClose();
        }
        requestNavigate(path, () => rrNavigate(path));
    };
    // A workspace switch drops the open page's draft too, so it asks the same
    // way. Picking the current workspace changes nothing and never asks.
    const handleSelectWorkspace = (id) => {
        if (id === selectedWorkspaceId) {
            onSelectWorkspace(id);
            return;
        }
        requestNavigate({ workspaceId: id }, () => onSelectWorkspace(id));
    };
    const handleBackToParent = onBackToParent
        ? () => requestNavigate({ workspaceId: 'parent' }, onBackToParent)
        : null;
    // D-05: hover/focus-prefetch the target route's chunk ahead of an
    // eventual click; silently no-ops for views without a prefetch mapping
    // (e.g. views already idle-prefetched, or ones with no lazy chunk).
    const handlePrefetchHover = (view) => {
        const thunk = HOVER_PREFETCH_BY_VIEW[view];
        if (thunk) prefetchRoute(thunk);
    };

    if (isMobile) {
        return (
            <>
                <div
                    className={`mobile-sidebar-backdrop ${isOpen ? 'mobile-sidebar-backdrop--open' : ''}`}
                    onClick={onClose}
                    aria-hidden={!isOpen}
                />
                <aside
                    className={`sidebar mobile-sidebar ${isOpen ? 'mobile-sidebar--open' : ''}`}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Navigation menu"
                >
                    <div className="sidebar__top mobile-sidebar__top">
                        <div className="sidebar__brand-mark">
                            <img
                                src="/images/keyflow-logo.generated.svg"
                                alt="KeyFlow logo"
                                className="sidebar__logo"
                            />
                            <span className="sidebar__brand">KeyFlow</span>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            className="mobile-sidebar__close"
                            aria-label="Close navigation"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="sidebar__icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                    <WorkspaceSelector
                        variant="sidebar"
                        workspaces={workspaces}
                        selectedWorkspaceId={selectedWorkspaceId}
                        onSelectWorkspace={(id) => { onClose(); handleSelectWorkspace(id); }}
                    />
                    {isViewingChildAsParent && (
                        <div className="sidebar__child-banner">
                            <div className="sidebar__child-banner-label">
                                <svg xmlns="http://www.w3.org/2000/svg" className="sidebar__icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                                <span>{tawName}</span>
                            </div>
                            {handleBackToParent && (
                                <button
                                    type="button"
                                    className="sidebar__child-banner-back"
                                    onClick={() => { onClose(); handleBackToParent(); }}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="sidebar__icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                    </svg>
                                    <span>Back to {twName}</span>
                                </button>
                            )}
                        </div>
                    )}
                    <nav className="sidebar__nav mobile-sidebar__nav">
                        {withCampaigns(mobileNavItems, item => (
                            <button
                                key={item.view}
                                type="button"
                                onClick={() => handleNavigate(item.view)}
                                className={`sidebar__item ${activeNavView === item.view ? 'sidebar__item--active' : ''}`}
                                aria-current={activeNavView === item.view ? 'page' : undefined}
                                aria-label={dueLabel(item) || undefined}
                            >
                                {item.icon}
                                <span>{item.label}</span>
                                {renderDueBadge(item)}
                                {renderReplyDot(item)}
                            </button>
                        ), true)}
                    </nav>
                </aside>
                {lockedSheet}
            </>
        );
    }

    return (
        <aside className={`sidebar shadow-md flex flex-col transition-all duration-300 ${isCollapsed ? 'w-[76px] sidebar--collapsed' : 'w-64'}`}>
            <button
                type="button"
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="sidebar__toggle"
                aria-label={isCollapsed ? 'Expand navigation' : 'Collapse navigation'}
                title={isCollapsed ? 'Expand navigation' : 'Collapse navigation'}
            >
                <svg xmlns="http://www.w3.org/2000/svg" className="sidebar__toggle-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    {isCollapsed ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.4} d="M9 5l7 7-7 7" />
                    ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.4} d="M15 19l-7-7 7-7" />
                    )}
                </svg>
            </button>
            <div className="sidebar__top">
                <div className="sidebar__brand-mark">
                    <img
                        src="/images/keyflow-logo.generated.svg"
                        alt="KeyFlow logo"
                        className="sidebar__logo"
                    />
                    <span className="sidebar__brand">KeyFlow</span>
                </div>
            </div>
            <WorkspaceSelector
                variant={isCollapsed ? 'sidebar-collapsed' : 'sidebar'}
                workspaces={workspaces}
                selectedWorkspaceId={selectedWorkspaceId}
                onSelectWorkspace={handleSelectWorkspace}
            />
            {isViewingChildAsParent && (
                <div className={`sidebar__child-banner ${isCollapsed ? 'sidebar__child-banner--collapsed' : ''}`}>
                    <div className="sidebar__child-banner-label">
                        <svg xmlns="http://www.w3.org/2000/svg" className="sidebar__icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        <span className="sidebar__item-label">{tawName}</span>
                    </div>
                    {handleBackToParent && (
                        <button
                            type="button"
                            className="sidebar__child-banner-back"
                            onClick={handleBackToParent}
                            title={isCollapsed ? `Back to ${twName}` : ''}
                            aria-label={isCollapsed ? `Back to ${twName}` : undefined}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="sidebar__icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                            <span className="sidebar__item-label">Back to {twName}</span>
                        </button>
                    )}
                </div>
            )}
            <nav className="sidebar__nav">
                {withCampaigns(visibleNavItems, item => (
                    <button
                        key={item.view}
                        type="button"
                        onClick={() => handleNavigate(item.view)}
                        onMouseEnter={() => handlePrefetchHover(item.view)}
                        onFocus={() => handlePrefetchHover(item.view)}
                        title={isCollapsed ? (dueLabel(item) || (showsReplyDot(item) ? `${item.label}, ${replyLabel}` : item.label)) : ''}
                        aria-label={dueLabel(item) || (isCollapsed ? (showsReplyDot(item) ? `${item.label}, ${replyLabel}` : item.label) : undefined)}
                        className={`sidebar__item ${isCollapsed && showsReplyDot(item) ? 'relative ' : ''}${activeNavView === item.view ? 'sidebar__item--active' : ''}`}
                        aria-current={activeNavView === item.view ? 'page' : undefined}
                    >
                        {item.icon}
                        <span className="sidebar__item-label">{item.label}</span>
                        {!isCollapsed && renderDueBadge(item)}
                        {renderReplyDot(item, isCollapsed)}
                    </button>
                ), false)}
            </nav>
            <div className="sidebar__footer">
                <button
                    type="button"
                    onClick={() => handleNavigate(SETTINGS_NAV_ITEM.view)}
                    onMouseEnter={() => handlePrefetchHover(SETTINGS_NAV_ITEM.view)}
                    onFocus={() => handlePrefetchHover(SETTINGS_NAV_ITEM.view)}
                    title={isCollapsed ? 'Settings' : ''}
                    aria-label={isCollapsed ? 'Settings' : undefined}
                    className={`sidebar__item ${activeNavView === SETTINGS_NAV_ITEM.view ? 'sidebar__item--active' : ''}`}
                    aria-current={activeNavView === SETTINGS_NAV_ITEM.view ? 'page' : undefined}
                >
                    {SETTINGS_NAV_ITEM.icon}
                    <span className="sidebar__item-label">Settings</span>
                </button>
                {!isCollapsed && (onOpenWhatsNew ? (
                    <BuildVersionLink
                        version={BUILD_VERSION}
                        onOpenWhatsNew={() => {
                            if (isMobile) onClose();
                            onOpenWhatsNew();
                        }}
                    />
                ) : (
                    <div
                        style={{
                            fontFamily: 'var(--font-mono)',
                            fontSize: 10,
                            letterSpacing: '0.02em',
                            color: 'var(--sidebar-text-quiet-faint)',
                            padding: '4px 12px 2px'
                        }}
                    >
                        Build {BUILD_VERSION}
                    </div>
                ))}
            </div>
            {lockedSheet}
        </aside>
    );
}

export default Sidebar;
